/**
 * EMZI NEXUS — Secure Storage Utility
 *
 * Auth tokens & user profile → localStorage (persists across tab close,
 * browser restart, and mobile tab-kill — required so the 365-day backend
 * session TTL is actually felt by users on long-running flows like SOA
 * drills which can span 15-20 mins with backgrounding.)
 *
 * Non-sensitive preferences (language, UI state) → localStorage as well.
 *
 * Hardening note: localStorage is XSS-readable. The app mitigates that via
 * input sanitization, controlled-render React (no dangerouslySetInnerHTML
 * on user-supplied data), and refresh-token rotation. The product
 * requirement of long persistent sessions outweighs the marginal benefit
 * of sessionStorage isolation.
 */

const SENSITIVE_KEYS = new Set([
  'nexus_access_token',
  'nexus_refresh_token',
  'nexus_user',
]);

export const secureStorage = {
  getItem(key) {
    return localStorage.getItem(key);
  },

  setItem(key, value) {
    localStorage.setItem(key, value);
  },

  removeItem(key) {
    localStorage.removeItem(key);
  },

  /**
   * One-time migration so users currently logged in via the OLD
   * sessionStorage scheme don't get logged out the moment they pick up
   * the new build. Copies any sensitive key found in sessionStorage
   * over to localStorage and removes the sessionStorage copy.
   */
  migrate() {
    SENSITIVE_KEYS.forEach((key) => {
      try {
        const ssValue = sessionStorage.getItem(key);
        if (ssValue !== null && localStorage.getItem(key) === null) {
          localStorage.setItem(key, ssValue);
        }
        // Always clear sessionStorage so we don't keep a stale copy
        sessionStorage.removeItem(key);
      } catch (_e) {
        // sessionStorage may throw in private mode — ignore
      }
    });
  },

  /** Clear all sensitive data (logout) */
  clearSensitive() {
    SENSITIVE_KEYS.forEach((key) => {
      localStorage.removeItem(key);
      try {
        sessionStorage.removeItem(key);
      } catch (_e) {
        // ignore
      }
    });
  },
};

export default secureStorage;
